import {
	endpointUsesHostFixture,
	fixtureLiesInFrontOfPortal,
	pointWithinPortalAperture,
} from './portalCollision.js';
import {
	portalApertureGuidePoint,
	portalNormal,
	signedPortalDistance,
	transformPortalPoint,
} from './portalMath.js';
import {canonicalPortalFixture} from './portalContactRouter.js';
import {portalCopyIsShadow} from './PortalGhostWorld.js';

const readVec = vector => ({
	x: typeof vector.get_x === 'function' ? vector.get_x() : vector.x,
	y: typeof vector.get_y === 'function' ? vector.get_y() : vector.y,
});

const endpointPosition = endpoint => readVec(endpoint.body.GetPosition());

const pointBelongsToPortalSide = (Box2D, fixture, point, endpoint) => {
	if(endpointUsesHostFixture(Box2D, endpoint, fixture) &&
		pointWithinPortalAperture(point, endpoint)) {
		return false;
	}
	return signedPortalDistance(
		point,
		endpointPosition(endpoint),
		endpoint.body.GetAngle(),
	) >= -0.01;
};

const castClosest = (Box2D, world, start, end, acceptFixture) => {
	let closest = null;
	const callback = Object.assign(new Box2D.JSRayCastCallback(), {
		ReportFixture(fixturePointer, pointPointer, normalPointer, fraction) {
			const fixture = typeof fixturePointer === 'number' ?
				Box2D.wrapPointer(fixturePointer, Box2D.b2Fixture) : fixturePointer;
			const pointVector = typeof pointPointer === 'number' ?
				Box2D.wrapPointer(pointPointer, Box2D.b2Vec2) : pointPointer;
			const normalVector = typeof normalPointer === 'number' ?
				Box2D.wrapPointer(normalPointer, Box2D.b2Vec2) : normalPointer;
			const point = readVec(pointVector);
			const normal = readVec(normalVector);
			if(!acceptFixture(fixture, point, normal, fraction)) return -1;
			closest = {fixture, point, normal, fraction};
			return fraction;
		},
	});
	world.RayCast(callback, start, end);
	Box2D.destroy(callback);
	return closest;
};

/**
 * Raycasts from a body that may be in portal transit. Real geometry only
 * counts in front of its entrance plane (and outside the opening on the
 * entrance host), ghost geometry only behind it, so a wheel that has crossed
 * the plane finds the far floor and a wheel still outside finds this one. A
 * ghost hit is reported with the real far-side fixture, in the caster's own
 * coordinates. Bodies not in transit never see ghosts.
 */
export const rayCastPortalAware = (
	Box2D,
	world,
	body,
	startVector,
	endVector,
	acceptFixture = () => true,
	travellingFixture = null,
) => {
	const start = readVec(startVector);
	const end = readVec(endVector);
	const candidate = body?._portalTransitCandidate;
	const fixtureAdmitted = !travellingFixture || candidate?.admittedFixtureKeys.has(
		Box2D.getPointer(travellingFixture),
	);
	const clipToSide = Boolean(candidate && fixtureAdmitted);
	const source = clipToSide ? candidate.source : null;
	const target = clipToSide ? candidate.target : null;
	// A fixture wholly in front of the entrance sees no far-side geometry yet.
	const seesGhosts = clipToSide && (!travellingFixture ||
		!fixtureLiesInFrontOfPortal(Box2D, travellingFixture, source));
	const startBox2D = new Box2D.b2Vec2(start.x, start.y);
	const endBox2D = new Box2D.b2Vec2(end.x, end.y);
	const hit = castClosest(Box2D, world, startBox2D, endBox2D,
		(rawFixture, point, normal, fraction) => {
			const rawBody = rawFixture.GetBody();
			if(rawBody.isPortalGhost) {
				const realFixture = rawFixture._portalGhostOf;
				if(!realFixture) return false;
				const shadow = portalCopyIsShadow(rawBody);
				const copyCandidate = rawBody._portalGhostOf?._portalTransitCandidate;
				if(candidate) {
					// A traveller's crossed part sees the far room through the copies
					// behind its entrance; an island coming the other way is whole.
					if(shadow && copyCandidate === candidate) return false;
					if(rawBody._portalGhostSide !== source) return false;
					if(shadow) return acceptFixture(realFixture, point, normal, fraction, 'ghost');
					if(!seesGhosts) return false;
					if(signedPortalDistance(point, endpointPosition(source), source.body.GetAngle()) > 0.01) {
						return false;
					}
					if(endpointUsesHostFixture(Box2D, target, realFixture) &&
						pointWithinPortalAperture(mapThroughPortal(point, source, target), target)) {
						return false;
					}
					return acceptFixture(realFixture, point, normal, fraction, 'ghost');
				}
				// A body outside transit only sees the emerged part of a shadow: a
				// vehicle driving onto a plank that came through a portal finds it.
				if(!shadow) return false;
				const exit = rawBody._portalGhostSide;
				if(signedPortalDistance(point, endpointPosition(exit), exit.body.GetAngle()) < -0.01) return false;
				return acceptFixture(realFixture, point, normal, fraction, 'shadow');
			}
			if(!acceptFixture(rawFixture, point, normal, fraction, 'primary')) return false;
			if(!clipToSide) return true;
			return pointBelongsToPortalSide(Box2D, rawFixture, point, source);
		});
	Box2D.destroy(endBox2D);
	Box2D.destroy(startBox2D);
	if(!hit) return null;
	const hitBody = hit.fixture.GetBody();
	return {
		...hit,
		fixture: canonicalPortalFixture(hit.fixture),
		representation: !hitBody.isPortalGhost ? 'primary' :
			portalCopyIsShadow(hitBody) && !candidate ? 'shadow' : 'ghost',
	};
};

const mapThroughPortal = (point, source, target) => transformPortalPoint(
	point,
	endpointPosition(source),
	source.body.GetAngle(),
	endpointPosition(target),
	target.body.GetAngle(),
);

/**
 * Resolves the visible pieces of a rope whose physical endpoints may change
 * canonical portal frames before the gun itself crosses. `rebased` means the
 * primary island currently lives in the original exit's frame. A direct result
 * is entirely visible beside the real anchor; a split result still has one
 * segment at each portal mouth.
 */
export const getPortalRopeVisualRoute = ({
	anchor,
	gun,
	entrance,
	exit,
	anchorReal,
	rebased = false,
	guideTangent = null,
	guideInset = 0,
}) => {
	if(!anchor || !gun || !anchorReal || !entrance?.body || !exit?.body ||
		entrance.destroyed || exit.destroyed) return null;

	const sourcePosition = endpointPosition(entrance);
	const targetPosition = endpointPosition(exit);
	const sourceAngle = entrance.body.GetAngle();
	const targetAngle = exit.body.GetAngle();
	const canonicalEndpoint = rebased ? exit : entrance;
	const canonicalPosition = rebased ? targetPosition : sourcePosition;
	const canonicalAngle = rebased ? targetAngle : sourceAngle;
	const gunDistance = signedPortalDistance(gun, canonicalPosition, canonicalAngle);

	if((!rebased && gunDistance < 0) || (rebased && gunDistance >= 0)) {
		return {
			mode: 'direct',
			anchorReal: {...anchorReal},
			gunPoint: rebased ? {...gun} : mapThroughPortal(gun, entrance, exit),
		};
	}

	let entryPoint;
	let exitPoint;
	if(Number.isFinite(guideTangent)) {
		const guide = portalApertureGuidePoint(
			sourcePosition,
			sourceAngle,
			guideTangent,
			entrance.apertureHalfLength,
			guideInset,
		);
		entryPoint = {x: guide.x, y: guide.y};
		exitPoint = mapThroughPortal(entryPoint, entrance, exit);
	} else if(!rebased) {
		const anchorDistance = signedPortalDistance(anchor, sourcePosition, sourceAngle);
		if(anchorDistance >= 0) return null;
		const t = anchorDistance / (anchorDistance - gunDistance);
		entryPoint = {
			x: anchor.x + (gun.x - anchor.x) * t,
			y: anchor.y + (gun.y - anchor.y) * t,
		};
		exitPoint = mapThroughPortal(entryPoint, entrance, exit);
	} else {
		const anchorDistance = signedPortalDistance(anchor, targetPosition, targetAngle);
		if(anchorDistance <= 0) return null;
		const t = anchorDistance / (anchorDistance - gunDistance);
		exitPoint = {
			x: anchor.x + (gun.x - anchor.x) * t,
			y: anchor.y + (gun.y - anchor.y) * t,
		};
		entryPoint = mapThroughPortal(exitPoint, exit, entrance);
	}

	return {
		mode: 'split',
		entrance,
		exit,
		entryPoint,
		exitPoint,
		anchorReal: {...anchorReal},
		gunPoint: rebased ? mapThroughPortal(gun, exit, entrance) : {...gun},
	};
};

const linkedPortalOf = endpoint => {
	const bridge = endpoint?.bridge;
	if(!bridge || bridge.destroyed) return null;
	const other = bridge.otherEndpoint(endpoint);
	return other && !other.destroyed ? other : null;
};

/**
 * Raycasts through linked portals: when the ray reaches a portal's cross-line
 * from the front inside its aperture, it continues from the linked portal in
 * the mapped direction. The hit is returned in real world coordinates together
 * with the crossings taken, so a caller can place an anchor on the far side and
 * still reason about where the ray entered. Callers animating a projectile may
 * opt into a traced miss, which preserves those crossings and the mapped end.
 */
export const rayCastThroughPortals = (
	Box2D,
	world,
	startVector,
	endVector,
	{
		acceptFixture = () => true,
		maxCrossings = 2,
		linkedEndpoint = linkedPortalOf,
		includeMissTrace = false,
	} = {},
) => {
	let start = readVec(startVector);
	let end = readVec(endVector);
	const crossings = [];
	const missTrace = point => includeMissTrace && crossings.length ? {
		fixture: null,
		point,
		normal: null,
		fraction: 1,
		crossings,
		missed: true,
	} : null;
	// A portal sits flush on its host surface, so a ray reaches the host and the
	// cross-line at the same fraction and the host must not win that tie: inside
	// the opening the host surface is the hole.
	const endpoints = [];
	for(let body = world.GetBodyList();
		Box2D.getPointer(body) !== Box2D.getPointer(Box2D.NULL);
		body = body.GetNext()) {
		if(body.isPortalSurface && body.portalEndpoint && !body.portalEndpoint.destroyed) {
			endpoints.push(body.portalEndpoint);
		}
	}
	const insidePortalOpening = (fixture, point) => endpoints.some(endpoint =>
		endpointUsesHostFixture(Box2D, endpoint, fixture) &&
		pointWithinPortalAperture(point, endpoint) &&
		Math.abs(signedPortalDistance(
			point,
			endpointPosition(endpoint),
			endpoint.body.GetAngle(),
		)) <= endpoint.surfaceHalfThickness + 0.02);

	for(let hop = 0; hop <= maxCrossings; hop++) {
		const direction = {x: end.x - start.x, y: end.y - start.y};
		const canCross = hop < maxCrossings;
		let solidHit = null;
		let portalHit = null;
		const startBox2D = new Box2D.b2Vec2(start.x, start.y);
		const endBox2D = new Box2D.b2Vec2(end.x, end.y);
		const callback = Object.assign(new Box2D.JSRayCastCallback(), {
			ReportFixture(fixturePointer, pointPointer, normalPointer, fraction) {
				const fixture = typeof fixturePointer === 'number' ?
					Box2D.wrapPointer(fixturePointer, Box2D.b2Fixture) : fixturePointer;
				const point = readVec(typeof pointPointer === 'number' ?
					Box2D.wrapPointer(pointPointer, Box2D.b2Vec2) : pointPointer);
				const normal = readVec(typeof normalPointer === 'number' ?
					Box2D.wrapPointer(normalPointer, Box2D.b2Vec2) : normalPointer);
				const body = fixture.GetBody();
				// Portal copies are never hook or bullet targets.
				if(body.isPortalGhost) return -1;
				const endpoint = body.portalEndpoint;
				if(body.isPortalSurface && endpoint) {
					// Only a linked portal's cross-line, reached from its front side and
					// inside the opening, carries the ray onward. Its sensor volume and
					// cap edges never stop a ray.
					if(!fixture._portalBackstop) return -1;
					const target = canCross ? linkedEndpoint(endpoint) : null;
					if(!target) return -1;
					const facing = portalNormal(endpoint.body.GetAngle());
					if(direction.x * facing.x + direction.y * facing.y >= 0) return -1;
					if(!pointWithinPortalAperture(point, endpoint)) return -1;
					if(!portalHit || fraction < portalHit.fraction) {
						portalHit = {endpoint, target, point, fraction};
					}
					return fraction;
				}
				if(fixture.IsSensor()) return -1;
				if(insidePortalOpening(fixture, point)) return -1;
				if(!acceptFixture(fixture, point, normal, fraction)) return -1;
				if(!solidHit || fraction < solidHit.fraction) {
					solidHit = {fixture, point, normal, fraction};
				}
				return fraction;
			},
		});
		world.RayCast(callback, startBox2D, endBox2D);
		Box2D.destroy(callback);
		Box2D.destroy(endBox2D);
		Box2D.destroy(startBox2D);

		if(!portalHit || (solidHit && solidHit.fraction <= portalHit.fraction)) {
			if(solidHit) return {...solidHit, crossings};
			return missTrace(end);
		}
		const {endpoint: source, target} = portalHit;
		crossings.push({
			source,
			target,
			entryPoint: portalHit.point,
			exitPoint: mapThroughPortal(portalHit.point, source, target),
		});
		const mappedEnd = mapThroughPortal(end, source, target);
		const exit = crossings[crossings.length - 1].exitPoint;
		// Leave the exit plane by a hair so the linked cross-line is not reported
		// again at fraction zero.
		const remaining = {x: mappedEnd.x - exit.x, y: mappedEnd.y - exit.y};
		const remainingLength = Math.hypot(remaining.x, remaining.y);
		if(remainingLength < 0.001) return missTrace(mappedEnd);
		const nudge = Math.min(0.01, remainingLength / 2);
		start = {
			x: exit.x + remaining.x / remainingLength * nudge,
			y: exit.y + remaining.y / remainingLength * nudge,
		};
		end = mappedEnd;
	}
	return missTrace(end);
};

// Maps a real far-side point back through the crossings a ray took, into the
// frame of the ray's origin: where a rope anchored beyond a portal appears to be
// from the shooter's side of it.
export const mapPointBackThroughCrossings = (point, crossings) => {
	let mapped = point;
	for(let index = crossings.length - 1; index >= 0; index--) {
		const {source, target} = crossings[index];
		mapped = mapThroughPortal(mapped, target, source);
	}
	return mapped;
};

export const mapPointThroughPortal = mapThroughPortal;
